import React, { useEffect, useState } from 'react'
import Modal from '../Modal'
import Button from '../../ui/buttons/Button'
import Input from '../../ui/inputs/Input'
import CurrencyInput from '../../ui/inputs/CurrencyInput'
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox'
import Select from '../../ui/selects/Select'
import Textarea from '../../ui/inputs/Textarea'
import { GlobeLock, X } from 'lucide-react'
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf'
import { snakecaseKeys } from '../../../utils/snakecaseKeys'
import { camelizeKeys } from '../../../utils/camelizeKeys'
import { useToast } from '../../ui/toast/ToastContext'

const CryptoModal = ({ clientId, cryptoId = null, scope, onClose, onSuccess }) => {
  const isEdit = !!cryptoId
  const { addToast } = useToast()

  const [form, setForm] = useState({
    name: '',
    cryptoType: '',
    value: '',
    isPersonal: false,
    notes: ''
  })

  const [cryptoTypeOptions, setCryptoTypeOptions] = useState([])

  // Carrega dados do criptoativo (modo edição)
  useEffect(() => {
    if (isEdit) {
      fetch(`/app/planner/client/${clientId}/dashboard/properties/cryptos/${cryptoId}?scope=${scope}`)
        .then(res => res.json())
        .then(data => setForm(camelizeKeys(data)))
        .catch(err => {
          console.error('Erro ao carregar criptoativo', err)
          addToast({ type: 'error', message: 'Erro ao carregar dados do criptoativo' })
        })
    }
  }, [isEdit, clientId, cryptoId])

  // Carrega options do select
  useEffect(() => {
    fetch('/app/shared/form_options')
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        setCryptoTypeOptions(camelData.cryptoTypes.map(type => ({
          label: type.label,
          value: type.value
        })))
      })
      .catch(err => {
        console.error('Erro ao carregar dados do formulário', err)
        addToast({ type: 'error', message: err.message })
      })
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleCheckbox = (name) => {
    setForm(prev => ({ ...prev, [name]: !prev[name] }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const payload = snakecaseKeys({ cryptoProperty: form })

    const url = isEdit
      ? `/app/planner/client/${clientId}/dashboard/properties/cryptos/${cryptoId}?scope=${scope}`
      : `/app/planner/client/${clientId}/dashboard/properties/cryptos?scope=${scope}`

    const method = isEdit ? 'PATCH' : 'POST'
    const successMsg = isEdit ? 'Criptoativo atualizado com sucesso!' : 'Criptoativo cadastrado com sucesso!'

    try {
      const res = await fetchWithCsrf(url, {
        method,
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      if (!res.ok) {
        const json = await res.json()
        throw new Error(json.message || 'Erro ao salvar criptoativo')
      }

      onClose()
      addToast({ type: 'success', message: successMsg })
      onSuccess()
    } catch (err) {
      addToast({ type: 'error', message: err.message })
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <GlobeLock size={24} className="text-tealMedium" strokeWidth={1.25} />
            {isEdit ? 'Editar Criptoativo' : 'Cadastrar Criptoativo'}
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <Input name="name" label="Identificação" value={form.name} onChange={handleChange} required />
          <Select name="cryptoType" label="Tipo do criptoativo" value={form.cryptoType} onChange={handleChange} options={cryptoTypeOptions} required />
          <div className='col-span-2'>
            <CurrencyInput name="value" label="Valor de mercado" value={form.value} onChange={handleChange} />
          </div>

          <Textarea
            name="notes"
            label="Observações"
            value={form.notes}
            onChange={handleChange}
            className="md:col-span-2"
            rows={4}
          />

          <CustomCheckbox
            name="isPersonal"
            label="É um bem particular"
            checked={form.isPersonal}
            onChange={() => handleCheckbox('isPersonal')}
            labelClassName='text-base'
          />

          <div className="md:col-span-2 flex justify-end items-center mt-1">
            <Button type="submit">
              {isEdit ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR CRIPTOATIVO'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default CryptoModal
